import { useEffect, useState } from 'react';
import { useNavigate, useParams, Navigate, Link, useSearchParams } from 'react-router-dom';
import api, { BillDetailDto } from '../services/milk-api';

function fmtDate(value: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function buildBillHtml(detail: BillDetailDto): string {
  const outstanding = (detail.totalAmount - detail.paidAmount).toFixed(2);
  const tc = getComputedStyle(document.documentElement).getPropertyValue('--tc').trim() || '#2E7D32';
  return `<!DOCTYPE html><html><head><title>Bill #${detail.billId}</title><style>
body{font-family:Arial,sans-serif;margin:20px;color:#333}
.header{text-align:center;border-bottom:2px solid ${tc};padding-bottom:10px;margin-bottom:15px}
.header h1{color:${tc};margin:0;font-size:1.4em}
.header p{margin:2px 0;font-size:0.85em;color:#666}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:15px;font-size:0.9em}
.info-grid div{padding:4px 0}
.info-grid strong{color:${tc}}
table{width:100%;border-collapse:collapse;margin:10px 0;font-size:0.85em}
th{background:${tc};color:white;padding:6px 8px;text-align:left}
td{padding:5px 8px;border-bottom:1px solid #eee}
.total-row{font-weight:bold;background:#f5f5f5}
.status-paid{color:${tc};font-weight:bold}
.status-unpaid{color:#D32F2F;font-weight:bold}
.footer{text-align:center;margin-top:20px;padding-top:10px;border-top:1px solid #ccc;font-size:0.8em;color:#888}
@media print{body{margin:10px}button{display:none!important}}
</style></head><body>
<div class="header">
<h1>${detail.dairyName}</h1>
${detail.dairyAddress ? `<p>${detail.dairyAddress}</p>` : ''}
<p>${detail.dairyEmail}</p>
${detail.dairyUpiId ? `<p><strong>UPI:</strong> ${detail.dairyUpiId}</p>` : ''}
</div>
<div class="info-grid">
<div><strong>Bill #:</strong> ${detail.billId}</div>
<div><strong>Date:</strong> ${new Date(detail.createdDate).toLocaleDateString()}</div>
<div><strong>Subscriber:</strong> ${detail.subscriberName}</div>
<div><strong>Bill No:</strong> #${detail.billId}</div>
<div><strong>Phone:</strong> ${detail.subscriberPhone || '-'}</div>
<div><strong>Period:</strong> ${new Date(detail.startDate).toLocaleDateString()} – ${new Date(detail.endDate).toLocaleDateString()}</div>
<div><strong>Status:</strong> <span class="status-${detail.status.toLowerCase()}">${detail.status}</span></div>
</div>
<h3 style="color:${tc};margin:10px 0 5px">Daily Breakdown</h3>
<table><thead><tr><th>Date</th><th>Milk Type</th><th>Qty (L)</th><th>₹/L</th><th>Amount</th><th>Extra</th><th>Status</th></tr></thead><tbody>
${detail.lineItems.map(item => `<tr><td>${new Date(item.date).toLocaleDateString()}</td><td>${item.milkTypeName}</td><td>${item.quantity}</td><td>₹${item.pricePerLiter}</td><td>₹${item.amount.toFixed(2)}</td><td>${item.isExtra ? '⭐' : ''}</td><td><span class="status-${(item.status || '').toLowerCase()}">${item.status || 'Delivered'}</span></td></tr>`).join('')}
<tr class="total-row"><td colspan="5" style="text-align:right">Total:</td><td>₹${detail.totalAmount.toFixed(2)}</td><td></td></tr>
</tbody></table>
${detail.payments && detail.payments.length > 0 ? `<h3 style="color:${tc};margin:10px 0 5px">Payments</h3><table><thead><tr><th>Date</th><th>Amount</th><th>Mode</th></tr></thead><tbody>${detail.payments.map(p => `<tr><td>${new Date(p.paymentDate).toLocaleDateString()}</td><td>₹${p.amount.toFixed(2)}</td><td>${p.mode}</td></tr>`).join('')}</tbody></table>` : ''}
${detail.dairyQrCodeBase64 || detail.dairyUpiId ? `<h3 style="color:${tc};margin:10px 0 5px">Payment QR / UPI</h3><div style="display:flex;gap:16px;align-items:center;border:1px solid #eee;padding:10px;border-radius:8px">${detail.dairyQrCodeBase64 ? `<img src="${detail.dairyQrCodeBase64}" alt="UPI QR" style="width:120px;height:120px;border:1px solid #ddd;border-radius:8px"/>` : ''}<div>${detail.dairyUpiId ? `<div><strong>UPI ID:</strong> ${detail.dairyUpiId}</div>` : ''}<div style="font-size:0.85em;color:#666">Scan and pay using any UPI app</div></div></div>` : ''}
<div class="info-grid" style="margin-top:10px;font-size:1em">
<div><strong>Total:</strong> ₹${detail.totalAmount.toFixed(2)}</div>
<div><strong>Paid:</strong> ₹${detail.paidAmount.toFixed(2)}</div>
<div><strong>Outstanding:</strong> <span class="status-${Number(outstanding) > 0 ? 'unpaid' : 'paid'}">₹${outstanding}</span></div>
</div>
<div class="footer"><p>Thank you for your patronage!</p><p>Generated on ${new Date().toLocaleString()}</p></div>
</body></html>`;
}

export default function BillDetailPage() {
  const { billId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [detail, setDetail] = useState<BillDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const id = Number(billId);
  const sig = searchParams.get('sig');
  const exp = searchParams.get('exp');

  useEffect(() => {
    if (!id) { setError('Invalid bill id'); setLoading(false); return; }

    // Bill detail is publicly accessible — no login required
    api.getPublicBillDetail(id, sig ?? undefined, exp ? Number(exp) : undefined)
      .then(res => {
        if (res.data) setDetail(res.data);
        else setError(res.message || 'Could not load bill');
      }).catch(() => setError('Could not load bill')).finally(() => setLoading(false));
  }, [id, sig, exp]);

  const handlePrint = () => {
    if (!detail) return;
    const printWin = window.open('', '_blank', 'width=800,height=900');
    if (!printWin) return;
    printWin.document.write(buildBillHtml(detail));
    printWin.document.close();
    printWin.focus();
    printWin.print();
  };

  const handleDownload = () => {
    if (!detail) return;
    const html = buildBillHtml(detail);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bill-${detail.billId}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const actionBtn: React.CSSProperties = {
    padding: '0.45rem 0.9rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '0.85rem', fontWeight: 600, color: 'white',
  };

  const hasAuth = !!api.getToken();

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <Link to={hasAuth ? "/" : "/login"} style={{ ...actionBtn, background: '#757575', textDecoration: 'none', display: 'inline-block', color: 'white' }}>
          {hasAuth ? '← Back to App' : 'Login to App'}
        </Link>
        {detail && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={handlePrint} style={{ ...actionBtn, background: '#1565C0' }}>🖨️ Print</button>
            <button onClick={handleDownload} style={{ ...actionBtn, background: 'var(--tc, #2E7D32)' }}>⬇ Download</button>
          </div>
        )}
      </div>

      {loading && (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>Loading bill...</div>
      )}

      {!loading && error && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#C62828' }}>
          {error}
          <div style={{ marginTop: '1rem' }}>
            <button onClick={() => navigate(hasAuth ? '/' : '/login', { replace: true })} style={{ ...actionBtn, background: '#757575' }}>
              {hasAuth ? '← Back to App' : 'Go to Login'}
            </button>
          </div>
        </div>
      )}

      {!loading && detail && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '1.5rem', boxShadow: '0 8px 30px rgba(0,0,0,0.08)' }}>
          <div style={{ textAlign: 'center', borderBottom: `2px solid var(--tc, #2E7D32)`, paddingBottom: '0.75rem', marginBottom: '1rem' }}>
            <h1 style={{ color: 'var(--tc, #2E7D32)', margin: 0, fontSize: '1.3rem' }}>{detail.dairyName}</h1>
            {detail.dairyAddress && <div style={{ color: '#666', fontSize: '0.85rem' }}>{detail.dairyAddress}</div>}
            <div style={{ color: '#666', fontSize: '0.85rem' }}>{detail.dairyEmail}</div>
            {detail.dairyUpiId && <div style={{ color: '#666', fontSize: '0.85rem' }}><strong>UPI:</strong> {detail.dairyUpiId}</div>}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <h2 style={{ color: 'var(--tc, #2E7D32)', margin: 0 }}>🧾 Bill #{detail.billId}</h2>
            <StatusBadge status={detail.status === 'Paid' ? 'paid' : 'unpaid'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.88rem' }}>
            <div><strong>Subscriber:</strong> {detail.subscriberName}</div>
            <div><strong>Phone:</strong> {detail.subscriberPhone || '—'}</div>
            <div><strong>Period:</strong> {fmtDate(detail.startDate)} – {fmtDate(detail.endDate)}</div>
            <div><strong>Created:</strong> {fmtDate(detail.createdDate)}</div>
            <div><strong>Total:</strong> <strong style={{ color: 'var(--tc, #2E7D32)' }}>₹{detail.totalAmount.toFixed(2)}</strong></div>
            <div><strong>Paid:</strong> ₹{detail.paidAmount.toFixed(2)}</div>
            <div><strong>Outstanding:</strong> <strong style={{ color: (detail.totalAmount - detail.paidAmount) > 0 ? '#D32F2F' : 'var(--tc, #2E7D32)' }}>₹{(detail.totalAmount - detail.paidAmount).toFixed(2)}</strong></div>
          </div>

          {detail.dairyQrCodeBase64 && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #E0E0E0', borderRadius: '10px', display: 'inline-block' }}>
              <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>Payment QR</div>
              <img src={detail.dairyQrCodeBase64} alt="Payment QR" style={{ width: '140px', height: '140px', borderRadius: '8px' }} />
            </div>
          )}

          <h3 style={{ color: 'var(--tc, #2E7D32)', margin: '1rem 0 0.5rem' }}>Daily Breakdown</h3>
          <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: 'var(--tc, #2E7D32)', color: 'white' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Milk Type</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Qty (L)</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>₹/L</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Amount</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Extra</th>
                </tr>
              </thead>
              <tbody>
                {detail.lineItems.map((item, i) => (
                  <tr key={i} style={{ background: i % 2 ? '#F7F9F8' : 'white', borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '5px 8px' }}>{fmtDate(item.date)}</td>
                    <td style={{ padding: '5px 8px' }}>{item.milkTypeName}</td>
                    <td style={{ padding: '5px 8px' }}>{item.quantity}</td>
                    <td style={{ padding: '5px 8px' }}>₹{item.pricePerLiter}</td>
                    <td style={{ padding: '5px 8px' }}>₹{item.amount.toFixed(2)}</td>
                    <td style={{ padding: '5px 8px' }}>{item.isExtra ? '⭐' : ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, background: '#F3F3F3' }}>
                  <td colSpan={4} style={{ padding: '6px 8px', textAlign: 'right' }}>Total:</td>
                  <td style={{ padding: '6px 8px' }}>₹{detail.totalAmount.toFixed(2)}</td>
                  <td style={{ padding: '6px 8px' }} />
                </tr>
              </tfoot>
            </table>

            <div className="mobile-card-list">
              {detail.lineItems.map((item, i) => (
                <div key={i} className="mobile-card-item">
                  <div className="mobile-card-header">
                    <span className="mobile-card-title">{fmtDate(item.date)}</span>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: item.isExtra ? '#F57F17' : '#888' }}>{item.isExtra ? '⭐ Extra' : item.status || ''}</span>
                  </div>
                  <div className="mobile-card-details">
                    <div><span className="mobile-card-detail-label">Milk: </span>{item.milkTypeName}</div>
                    <div><span className="mobile-card-detail-label">Qty: </span>{item.quantity} L</div>
                    <div><span className="mobile-card-detail-label">₹/L: </span>₹{item.pricePerLiter}</div>
                    <div><span className="mobile-card-detail-label">Amount: </span><strong>₹{item.amount.toFixed(2)}</strong></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {detail.payments && detail.payments.length > 0 && (
            <>
              <h3 style={{ color: 'var(--tc, #2E7D32)', margin: '1rem 0 0.5rem' }}>Payments</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--tc, #2E7D32)', color: 'white' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Amount</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.payments.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '5px 8px' }}>{fmtDate(p.paymentDate)}</td>
                      <td style={{ padding: '5px 8px' }}>₹{p.amount.toFixed(2)}</td>
                      <td style={{ padding: '5px 8px' }}>{p.mode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {detail.subscriptionChanges && detail.subscriptionChanges.length > 0 && (
            <>
              <h3 style={{ color: 'var(--tc, #2E7D32)', margin: '1rem 0 0.5rem' }}>Subscription Changes</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--tc, #2E7D32)', color: 'white' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Old</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>New</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.subscriptionChanges.map((c, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '5px 8px' }}>{fmtDate(c.effectiveDate)}</td>
                      <td style={{ padding: '5px 8px' }}>{c.oldMilkTypeName} × {c.oldQuantity}L</td>
                      <td style={{ padding: '5px 8px' }}>{c.newMilkTypeName} × {c.newQuantity}L</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #eee', color: '#888', fontSize: '0.8rem' }}>
            Thank you for your patronage!
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    paid: { bg: '#E8F5E9', color: '#2E7D32', label: 'Paid' },
    unpaid: { bg: '#FFEBEE', color: '#C62828', label: 'Unpaid' },
    revoked: { bg: '#F3E5F5', color: '#6A1B9A', label: 'Revoked' },
  };
  const s = map[status] || { bg: '#F5F5F5', color: '#666', label: status };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600, display: 'inline-block' }}>
      {s.label}
    </span>
  );
}